import axiosInstance from '../../api/api';
import { API_ROUTES } from '../../config/api-routes';

const DASHBOARD_API_TIMEOUT_MS = 10000;

type ViewerTypeRow = { type?: string; count?: string | number };
type DashboardPayload = {
  totals?: {
    views?: number;
    uniqueVisitors?: number;
    uniqueRecruiters?: number;
    likes?: number;
    engagements?: number;
  };
  profileStrength?: number;
  breakdowns?: {
    viewerTypes?: ViewerTypeRow[];
  };
};

type EngagementSnapshot = {
  profile_views?: number;
  unique_recruiter_views?: number;
  unique_engaged_recruiters?: number;
};

type CandidateSnapshot = {
  overview?: { profile_strength?: number };
  engagement?: EngagementSnapshot;
  totals?: DashboardPayload['totals'];
  profileStrength?: number;
};

type DashboardResponse = {
  success?: boolean;
  data?: CandidateSnapshot & DashboardPayload;
  '30days'?: { data?: DashboardPayload };
  '90days'?: { data?: DashboardPayload };
};

export type CandidateDashboardMetrics = {
  profileViews: number;
  /** Small badge: unique recruiters who viewed your profile. */
  profileViewsBadge: number;
  interestedRecruiters: number;
  /** Badge: same as interested recruiters (unique engaged). */
  interestedBadge: number;
  /** 0–100 from analytics engine; falls back to onboarding readiness. */
  screeningReadiness: number;
};

function parseDashboardMetricsPayload(
  data: DashboardResponse | CandidateSnapshot | DashboardPayload | undefined
): CandidateDashboardMetrics | null {
  if (!data) return null;
  const snapshot = ((data as DashboardResponse)?.data ?? data) as CandidateSnapshot &
    DashboardPayload;
  const fromEngagement = metricsFromCandidateSnapshot(snapshot);
  if (fromEngagement) return fromEngagement;

  const main = snapshot?.totals ? snapshot : (data as DashboardResponse)?.data;
  const last30 = (data as DashboardResponse)?.['30days']?.data;
  if (main?.totals) {
    return metricsFromLegacyDashboard(main, last30);
  }
  return null;
}

function metricsFromLegacyDashboard(
  main: DashboardPayload,
  last30?: DashboardPayload
): CandidateDashboardMetrics {
  const interestedRecruiters = Number(
    main.totals?.uniqueRecruiters ?? main.totals?.uniqueVisitors ?? 0
  );
  const views30 = Number(last30?.totals?.views ?? main.totals?.views ?? 0);
  const unique30 = Number(
    last30?.totals?.uniqueRecruiters ??
      last30?.totals?.uniqueVisitors ??
      main.totals?.uniqueRecruiters ??
      main.totals?.uniqueVisitors ??
      0
  );

  return {
    profileViews: Number(main.totals?.views ?? 0),
    profileViewsBadge: views30,
    interestedRecruiters,
    interestedBadge: unique30,
    screeningReadiness: Math.max(0, Math.min(100, Math.round(Number(main.profileStrength ?? 0)))),
  };
}

function metricsFromCandidateSnapshot(
  snapshot: CandidateSnapshot
): CandidateDashboardMetrics | null {
  const engagement = snapshot.engagement;
  if (!engagement) return null;

  const profileViews = Number(engagement.profile_views ?? 0);
  const uniqueViewers = Number(engagement.unique_recruiter_views ?? 0);
  // Interested = recruiters who actually opened the public profile (not likes-only test rows).
  const interestedRecruiters = uniqueViewers;

  return {
    profileViews,
    profileViewsBadge: uniqueViewers,
    interestedRecruiters,
    interestedBadge: uniqueViewers,
    screeningReadiness: Math.max(
      0,
      Math.min(
        100,
        Math.round(Number(snapshot.overview?.profile_strength ?? snapshot.profileStrength ?? 0))
      )
    ),
  };
}

/**
 * Dashboard headline metrics — aligned with Analytics candidate snapshot engagement.
 */
export async function fetchCandidateDashboardMetrics(): Promise<CandidateDashboardMetrics | null> {
  try {
    const { data } = await axiosInstance.get<DashboardResponse>(API_ROUTES.ANALYTICS.DASHBOARD, {
      timeout: 20000,
    });
    const parsed = parseDashboardMetricsPayload(data);
    if (parsed) {
      return parsed;
    }
  } catch (e) {
    console.warn('fetchCandidateDashboardMetrics primary route failed', e);
  }

  // Fallback path: candidate snapshot endpoint powers other dashboard surfaces
  // and is often lighter/less error-prone than the full analytics dashboard.
  try {
    const { data } = await axiosInstance.get<{ data?: CandidateSnapshot }>(
      API_ROUTES.ANALYTICS.CANDIDATE_SNAPSHOT,
      { timeout: 20000 }
    );
    const parsed = parseDashboardMetricsPayload((data?.data ?? data) as CandidateSnapshot);
    if (parsed) {
      return parsed;
    }
  } catch (e) {
    console.warn('fetchCandidateDashboardMetrics fallback route failed', e);
  }

  return null;
}

export type TrendCardContent = {
  id: string;
  title: string;
  summary: string;
  actionText: string;
  sourceLabel?: string;
  sourceUrl?: string;
};

export type DashboardTrendsFile = {
  trends: TrendCardContent[];
};

/** Candidate dashboard trends from backend API. */
export async function fetchDashboardTrends(): Promise<TrendCardContent[]> {
  try {
    const { data } = await axiosInstance.get<{ trends: TrendCardContent[] }>(
      API_ROUTES.DASHBOARD.TRENDS
    );
    return Array.isArray(data?.trends) ? data.trends : [];
  } catch {
    return [];
  }
}

export type LearningCard = {
  id?: string;
  kind: 'webinar' | 'certificate';
  title: string;
  subtitle?: string;
  meta?: string[];
  ctaLabel: string;
  ctaHref?: string;
  timeBadge?: string;
  badgeLabel?: string;
};

export async function fetchDashboardLearning(): Promise<LearningCard[]> {
  try {
    const { data } = await axiosInstance.get(API_ROUTES.DASHBOARD.CONTINUED_LEARNING, {
      timeout: DASHBOARD_API_TIMEOUT_MS,
    });
    if (!Array.isArray(data)) return [];
    return data.map((item: any) => ({
      id: item.id,
      kind: item.item_type === 'certificate' ? 'certificate' : 'webinar',
      title: item.title,
      meta: [
        ...(item.start_datetime
          ? [
              `${new Date(item.start_datetime).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })} • ${new Date(item.start_datetime).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
              })}${item.timezone ? ` ${item.timezone}` : ''}`,
            ]
          : []),
        `Hosted by: ${item.provider_name}`,
        ...(item.relevance_note ? [item.relevance_note] : []),
      ],
      ctaLabel: item.cta_label || (item.item_type === 'certificate' ? 'View Program' : 'Register'),
      ctaHref: item.source_url,
      badgeLabel: item.badge_label,
    }));
  } catch {
    return [];
  }
}

export async function trackLearningClick(learningId: string): Promise<void> {
  if (!learningId) return;
  try {
    await axiosInstance.post(`${API_ROUTES.DASHBOARD.CONTINUED_LEARNING}/${learningId}/click`);
  } catch {
    // non-blocking analytics
  }
}

/** Activity list item for Recent Activity feed. */
export type DashboardActivityItem = {
  id: string;
  text: string;
  time: string;
  icon: 'pencil' | 'chat' | 'plus' | 'person' | 'eye' | 'heart' | 'default';
  /** Who initiated / who the item is about */
  role: 'recruiter' | 'candidate' | 'system';
  /** Small chips: View, Like, Message, Expand */
  interactions: string[];
  /** ISO time for sorting */
  sortKey: string;
  /** Optional link (e.g. messages) */
  href?: string;
};

type ActivityApiItem = {
  id: string;
  message: string;
  timestamp: string;
  category: string;
  ctaLabel?: string | null;
  ctaHref?: string | null;
};

type ActivityApiResponse = {
  success: boolean;
  items: ActivityApiItem[];
};

function formatActivityTime(iso: string): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday =
      d.getFullYear() === yesterday.getFullYear() &&
      d.getMonth() === yesterday.getMonth() &&
      d.getDate() === yesterday.getDate();
    const timeStr = d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
    if (sameDay) return `Today at ${timeStr}`;
    if (isYesterday) return `Yesterday at ${timeStr}`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ` at ${timeStr}`;
  } catch {
    return '';
  }
}

function categoryIcon(category: string): DashboardActivityItem['icon'] {
  const c = category.toLowerCase();
  if (c.includes('asked_question')) return 'chat';
  if (c.includes('prompt')) return 'pencil';
  if (c.includes('upgraded')) return 'plus';
  if (c.includes('reference')) return 'person';
  if (c.includes('viewed_profile')) return 'eye';
  if (c.includes('like')) return 'heart';
  return 'default';
}

function categoryRole(category: string): DashboardActivityItem['role'] {
  const c = category.toLowerCase();
  if (c.startsWith('recruiter.')) return 'recruiter';
  if (c.startsWith('user.')) return 'candidate';
  return 'system';
}

function categoryInteractions(category: string): string[] {
  const c = category.toLowerCase();
  if (c.includes('asked_question')) return ['Message'];
  if (c.includes('viewed_profile')) return ['View'];
  if (c.includes('requested_access') || c.includes('requested_interview')) return ['Action'];
  return ['Update'];
}

/** Max rows in Recent Activity on the dashboard (keeps the column compact). */
export const DASHBOARD_ACTIVITY_LIMIT = 5;

/** Max rows on the full Recent Activity page. */
export const ACTIVITY_PAGE_LIMIT = 100;

export async function fetchDashboardActivities(
  limit: number = DASHBOARD_ACTIVITY_LIMIT
): Promise<DashboardActivityItem[]> {
  try {
    const { data } = await axiosInstance.get<ActivityApiResponse>(API_ROUTES.DASHBOARD.ACTIVITIES, {
      params: { limit },
      timeout: DASHBOARD_API_TIMEOUT_MS,
    });
    const rows = Array.isArray(data?.items) ? data.items : [];
    return rows.map((item) => ({
      id: item.id,
      text: item.message,
      time: formatActivityTime(item.timestamp),
      icon: categoryIcon(item.category),
      role: categoryRole(item.category),
      interactions: categoryInteractions(item.category),
      sortKey: item.timestamp,
      href: item.ctaHref || undefined,
    }));
  } catch {
    return [];
  }
}
